import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import BrandHeader from "../../components/BrandHeader";
import api from "../../lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast.error("Invalid or missing reset token.");
      navigate("/auth/login");
    }
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { 
        token: token, 
        new_password: newPassword 
      });
      toast.success("Password successfully reset! You can now log in.");
      navigate("/auth/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset password. The token may be expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) return null;

  return (
    <div className="px-6 pt-12 pb-10 fade-up max-w-xl mx-auto">
      <BrandHeader size="lg" />
      <div className="h-16" />

      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none text-zinc-950 mb-4">
        Create new <span className="text-emerald-500">password</span>.
      </h1>
      <p className="text-zinc-500 mb-8 font-medium">
        Please enter your new password below. Make sure it's at least 6 characters long.
      </p>

      <form onSubmit={submit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-bold text-zinc-900">New Password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              required
              className="ls-input w-full pr-12"
              placeholder="At least 6 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute p-2 transition-colors -translate-y-1/2 right-2 top-1/2 text-zinc-400 hover:text-zinc-600 focus:outline-none"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="h-14 w-full mt-2 bg-zinc-950 text-white text-lg font-bold rounded-lg flex items-center justify-center transition-colors hover:bg-zinc-800 active:scale-[0.98] disabled:opacity-60"
        >
          {loading ? "Updating..." : "Save new password"}
        </button>
      </form>
    </div>
  );
}